import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { withFirebase } from '../Fb'
import PersonAndOrganisationSearch from '../Inputs/PersonAndOrganisationSearch'
import { parseTicketQuantity } from '../../eventtickets'
import {
  PAYMENT_TYPES,
  creditWarning,
  daysInRange,
  errorText,
  fiDate,
  formatEuro,
  isValidDate,
  parseEuroToCents,
  unpaidCents,
} from '../utils/dayresources'

// Payer as stored on a day reservation, from a PersonAndOrganisationSearch hit.
const toPayer = (hit) => {
  if (!hit) return null
  if (hit.businessId || hit.type === 'organisation') {
    return { id: hit.id, type: 'organisation', name: hit.name || '', businessId: hit.businessId || '', email: hit.email || '' }
  }
  return {
    id: hit.id,
    type: 'person',
    name: [hit.firstname, hit.lastname].filter(Boolean).join(' '),
    firstname: hit.firstname || '',
    lastname: hit.lastname || '',
    email: hit.email || '',
    phone: hit.phone || '',
  }
}

// The search shows its selection as chips built from firstname/lastname or name+businessId.
const toSearchValue = (payer) => {
  if (!payer) return []
  if (payer.type === 'organisation') return [{ id: payer.id, name: payer.name, businessId: payer.businessId || ' ', email: payer.email }]
  return [{ id: payer.id, firstname: payer.name, lastname: '', email: payer.email }]
}

const payerDetail = (payer) => {
  if (!payer) return ''
  if (payer.type === 'organisation') return payer.businessId ? 'Y-tunnus ' + payer.businessId : 'Organisaatio'
  return payer.email || ''
}

// New booking (booking null; `initial` = { resourceId, date }) or editing an existing one.
// The backend (dayreservations-call) decides capacity and price; `quote` previews both.
function DayReservationDialog({ open, onClose, onSaved, firebase, resources, booking, initial }) {
  const editing = !!(booking && booking.id)
  const [resourceId, setResourceId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [quantityText, setQuantityText] = useState('1')
  const [payer, setPayer] = useState(null)
  const [note, setNote] = useState('')
  const [overrideText, setOverrideText] = useState('')
  const [addPayment, setAddPayment] = useState(false)
  const [paymentType, setPaymentType] = useState('cash')
  const [quote, setQuote] = useState(null)
  const [quoting, setQuoting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [warning, setWarning] = useState(null)
  const [newPaymentText, setNewPaymentText] = useState('')
  const [newPaymentType, setNewPaymentType] = useState('cash')
  const [confirmCancel, setConfirmCancel] = useState(false)
  const quoteSeq = useRef(0)

  useEffect(() => {
    if (!open) return
    if (editing) {
      setResourceId(booking.resourceId)
      setStartDate(booking.startDate)
      setEndDate(booking.endDate)
      setQuantityText(String(booking.quantity))
      setPayer(booking.payer || null)
      setNote(booking.note || '')
      setOverrideText(booking.overrideTotal !== undefined && booking.overrideTotal !== null ? (booking.overrideTotal / 100).toFixed(2).replace('.', ',') : '')
    } else {
      setResourceId((initial && initial.resourceId) || '')
      setStartDate((initial && initial.date) || '')
      setEndDate((initial && initial.date) || '')
      setQuantityText('1')
      setPayer(null)
      setNote('')
      setOverrideText('')
    }
    setAddPayment(false)
    setPaymentType('cash')
    setQuote(null)
    setError(null)
    setWarning(null)
    setNewPaymentText('')
    setConfirmCancel(false)
    // Keyed on the booking id, not the object: the live listener hands a new object after every save,
    // and resetting then would wipe the credit warning the admin has to see.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking && booking.id, initial])

  const searchValues = useMemo(() => toSearchValue(payer), [payer])
  const resource = useMemo(() => (resources || []).find((r) => r.id === resourceId) || null, [resources, resourceId])
  const capacity = resource ? resource.quantity || 1 : 1
  const quantity = parseTicketQuantity(quantityText)
  const overrideCents = overrideText.trim() === '' ? null : parseEuroToCents(overrideText)
  const overrideInvalid = overrideText.trim() !== '' && overrideCents === null
  const datesValid = isValidDate(startDate) && isValidDate(endDate) && endDate >= startDate
  const days = datesValid ? daysInRange(startDate, endDate).length : 0
  const quantityInvalid = quantity === null || quantity > capacity
  const cancelled = editing && booking.state === 'cancelled'
  const canQuote = !!resource && datesValid && !quantityInvalid && !overrideInvalid

  // Live price and availability from the backend.
  useEffect(() => {
    if (!open || !canQuote || cancelled) {
      setQuote(null)
      return undefined
    }
    const seq = ++quoteSeq.current
    setQuoting(true)
    const timer = setTimeout(async () => {
      const result = await firebase.quoteDayReservation({
        resourceId,
        startDate,
        endDate,
        quantity,
        id: editing ? booking.id : undefined,
        overrideTotal: overrideCents === null ? undefined : overrideCents,
      })
      if (seq !== quoteSeq.current) return
      setQuoting(false)
      setQuote(result)
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resourceId, startDate, endDate, quantity, overrideCents, canQuote, cancelled])

  const full = quote && !quote.error && quote.fullDates && quote.fullDates.length > 0
  const canSave = canQuote && !!payer && !saving && !quoting && quote && !quote.error && !full && !cancelled

  const finish = (result) => {
    const warn = creditWarning(result)
    if (onSaved) onSaved(result)
    if (warn) {
      setWarning(warn)
    } else if (onClose) {
      onClose()
    }
  }

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setError(null)
    let result
    if (editing) {
      result = await firebase.updateDayReservation({
        id: booking.id,
        startDate,
        endDate,
        quantity,
        note,
        payer,
        overrideTotal: overrideCents,
      })
    } else {
      result = await firebase.createDayReservation({
        resourceId,
        startDate,
        endDate,
        quantity,
        payer,
        note,
        overrideTotal: overrideCents === null ? undefined : overrideCents,
        payment: addPayment && quote.total > 0 ? { type: paymentType, amount: quote.total } : undefined,
      })
    }
    setSaving(false)
    if (!result || result.error) {
      setError(errorText(result) || 'Tallennus epäonnistui.')
      return
    }
    finish(result)
  }

  const handleCancelBooking = async () => {
    setSaving(true)
    setError(null)
    const result = await firebase.cancelDayReservation(booking.id)
    setSaving(false)
    setConfirmCancel(false)
    if (!result || result.error) {
      setError(errorText(result) || 'Peruutus epäonnistui.')
      return
    }
    finish({ ...result, total: 0 })
  }

  const handleAddPayment = async () => {
    const amount = parseEuroToCents(newPaymentText)
    if (!amount) {
      setError('Anna maksun summa euroina.')
      return
    }
    setSaving(true)
    setError(null)
    const result = await firebase.addDayReservationPayment(booking.id, { amount, type: newPaymentType })
    setSaving(false)
    if (!result || result.error) {
      setError(errorText(result) || 'Maksun lisäys epäonnistui.')
      return
    }
    setNewPaymentText('')
    if (onSaved) onSaved(result)
  }

  const payments = (editing && booking.payments) || []

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle>{editing ? 'Päivävaraus' : 'Uusi päivävaraus'}</DialogTitle>
      <DialogContent>
        {cancelled && (
          <Alert severity='info' sx={{ mb: 2 }}>
            Varaus on peruttu.
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            select
            size='small'
            label='Päiväresurssi'
            value={resource ? resourceId : ''}
            onChange={(e) => setResourceId(e.target.value)}
            disabled={editing}
            helperText={resource ? formatEuro(resource.pricePerDay) + ' / päivä / kpl, kapasiteetti ' + capacity : ''}
          >
            {(resources || []).map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.displayName}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size='small'
              type='date'
              label='Alkaa'
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value)
                if (!endDate || e.target.value > endDate) setEndDate(e.target.value)
              }}
              InputLabelProps={{ shrink: true }}
              disabled={cancelled}
            />
            <TextField
              size='small'
              type='date'
              label='Päättyy (viimeinen päivä)'
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || undefined }}
              error={!!startDate && !!endDate && endDate < startDate}
              helperText={datesValid ? days + (days === 1 ? ' päivä' : ' päivää') : ''}
              disabled={cancelled}
            />
            <TextField
              size='small'
              type='number'
              label='Määrä'
              value={quantityText}
              onChange={(e) => setQuantityText(e.target.value)}
              inputProps={{ min: 1, max: capacity, step: 1 }}
              error={quantityInvalid}
              helperText={quantityInvalid ? '1–' + capacity : ''}
              sx={{ width: 110 }}
              disabled={cancelled}
            />
          </Box>
          <Box>
            <PersonAndOrganisationSearch
              label='Maksaja'
              placeholder='Etsi nimellä tai sähköpostilla'
              values={searchValues}
              disabled={cancelled}
              onChange={(e, values) => setPayer(values && values.length > 0 ? toPayer(values[values.length - 1]) : null)}
            />
            {payer && (
              <Typography variant='caption' color='textSecondary'>
                {payer.name} {payerDetail(payer) ? '· ' + payerDetail(payer) : ''}
              </Typography>
            )}
          </Box>
          <TextField size='small' label='Lisätieto' value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} disabled={cancelled} />
          <TextField
            size='small'
            label='Oma hinta (€, valinnainen)'
            value={overrideText}
            onChange={(e) => setOverrideText(e.target.value)}
            error={overrideInvalid}
            helperText={overrideInvalid ? 'Anna summa euroina, esim. 120,00' : 'Tyhjä = päivät × määrä × päivähinta'}
            disabled={cancelled}
          />
          {!editing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Checkbox checked={addPayment} onChange={(e) => setAddPayment(e.target.checked)} />}
                label='Lisää myös maksu (koko summa)'
              />
              {addPayment && (
                <TextField select size='small' label='Maksutapa' value={paymentType} onChange={(e) => setPaymentType(e.target.value)} sx={{ minWidth: 140 }}>
                  {PAYMENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          )}

          {quoting && <Typography variant='body2'>Lasketaan hintaa…</Typography>}
          {!quoting && quote && !quote.error && (
            <Alert severity={full ? 'error' : 'info'}>
              {full
                ? 'Täynnä: ' + quote.fullDates.map(fiDate).join(', ') + '. Valitse muut päivät tai pienempi määrä.'
                : 'Hinta ' + formatEuro(quote.total) + ' (' + quote.days + ' pv × ' + quantity + ' kpl).'}
            </Alert>
          )}
          {!quoting && quote && quote.error && <Alert severity='error'>{errorText(quote)}</Alert>}
          {!payer && !cancelled && <Typography variant='caption' color='textSecondary'>Valitse maksaja, jotta varauksen voi tallentaa ja laskuttaa.</Typography>}
          {error && <Alert severity='error'>{error}</Alert>}
          {warning && <Alert severity='warning'>{warning}</Alert>}
        </Box>

        {editing && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant='subtitle1'>Maksut</Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Hinta {formatEuro(booking.total)}, maksamatta {formatEuro(unpaidCents(booking))}. Maksamaton osuus näkyy Laskutettavaa-sivulla.
            </Typography>
            {payments.length > 0 && (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Aika</TableCell>
                    <TableCell>Tapa</TableCell>
                    <TableCell align='right'>Summa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p, i) => (
                    <TableRow key={p.id || i}>
                      <TableCell>{p.timestamp}</TableCell>
                      <TableCell>{p.typelabel || p.type}{p.invoicenumber ? ' #' + p.invoicenumber : ''}</TableCell>
                      <TableCell align='right'>{formatEuro(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!cancelled && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
                <TextField size='small' label='Summa (€)' value={newPaymentText} onChange={(e) => setNewPaymentText(e.target.value)} sx={{ width: 130 }} />
                <TextField select size='small' label='Maksutapa' value={newPaymentType} onChange={(e) => setNewPaymentType(e.target.value)} sx={{ minWidth: 140 }}>
                  {PAYMENT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant='outlined' size='small' disabled={saving || !newPaymentText} onClick={handleAddPayment}>
                  Lisää maksu
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {editing && !cancelled && !warning && (
          confirmCancel ? (
            <>
              <Typography variant='body2' sx={{ mr: 'auto', ml: 1 }}>Perutaanko varaus? Maksut säilyvät.</Typography>
              <Button onClick={() => setConfirmCancel(false)} disabled={saving}>Ei</Button>
              <Button color='error' onClick={handleCancelBooking} disabled={saving}>Peru varaus</Button>
            </>
          ) : (
            <Button color='error' onClick={() => setConfirmCancel(true)} disabled={saving} sx={{ mr: 'auto' }}>
              Peru varaus
            </Button>
          )
        )}
        <Button onClick={onClose} disabled={saving}>{warning ? 'Sulje' : 'Peruuta'}</Button>
        {!warning && !cancelled && (
          <Button variant='contained' onClick={handleSave} disabled={!canSave}>
            Tallenna {saving && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default withFirebase(DayReservationDialog)
